// UTM attribution for marketing surfaces.
//
// withUtm() appends utm_source / utm_medium / utm_campaign to a target URL
// for outbound CTAs from segment, comparison, and case-study pages.
// captureUtmFromLocation() — called once at page mount — reads the
// inbound utm_* params from the current URL and persists them in
// sessionStorage so the signup flow can attribute the lead even after
// navigating between marketing surfaces.
//
// Why sessionStorage and not localStorage:
//   • Attribution should be per-visit, not "forever".
//   • Clearing on tab close mirrors how Google Analytics treats a session.

const STORAGE_KEY = "delapresse_utm";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
type UtmKey = (typeof UTM_KEYS)[number];

export type UtmParams = Partial<Record<UtmKey, string>>;

export interface WithUtmOptions {
  source: string;
  medium?: string;
  campaign: string;
  term?: string;
  content?: string;
}

/** Append utm_* params to a relative or absolute URL. */
export function withUtm(target: string, opts: WithUtmOptions): string {
  const params = new URLSearchParams();
  params.set("utm_source", opts.source);
  params.set("utm_medium", opts.medium ?? "cta");
  params.set("utm_campaign", opts.campaign);
  if (opts.term) params.set("utm_term", opts.term);
  if (opts.content) params.set("utm_content", opts.content);

  const [path, existingQuery = ""] = target.split("?");
  const merged = new URLSearchParams(existingQuery);
  for (const [k, v] of params) merged.set(k, v);
  return `${path}?${merged.toString()}`;
}

function safeSessionStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Read the current URL's utm_* params and persist them in sessionStorage.
 * Idempotent: if a previous capture already wrote a value for a key,
 * subsequent navigations won't overwrite it (first-touch attribution).
 */
export function captureUtmFromLocation(search: string): UtmParams {
  const storage = safeSessionStorage();
  const incoming = new URLSearchParams(search);
  const captured: UtmParams = {};

  for (const key of UTM_KEYS) {
    const value = incoming.get(key);
    if (value) captured[key] = value;
  }
  if (Object.keys(captured).length === 0 || !storage) return readStoredUtm();

  const existing = readStoredUtm();
  // First-touch wins; only fill keys that aren't already stored.
  const merged: UtmParams = { ...captured, ...existing };
  storage.setItem(STORAGE_KEY, JSON.stringify(merged));
  return merged;
}

export function readStoredUtm(): UtmParams {
  const storage = safeSessionStorage();
  if (!storage) return {};
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as UtmParams;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function clearStoredUtm(): void {
  safeSessionStorage()?.removeItem(STORAGE_KEY);
}
